import React, {
  createContext, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { isAirtableConfigured } from '../config/airtable';
import snapshot from '../data/content.json';
import {
  fallbackEditorial,
  fallbackEvents,
  fallbackGallery,
  fallbackFilms,
  fallbackNavigation,
  fallbackPublications,
  fallbackSiteContent,
  fallbackTeaching,
  fallbackTwentyEightDaysStills,
} from '../data/fallbackContent';
import { fetchContent } from '../services/airtable';
import {
  pauseFetching, readCache, refreshRequested, saveCache,
} from '../services/contentCache';

const ContentContext = createContext(null);

/*
 * Where the pages get their words and pictures.
 *
 * Airtable is the source: whatever the base says wins. Underneath it sits the
 * content bundled with the app, which shows through wherever the base is
 * silent — a table with no rows, a row left blank, a table that could not be
 * read, or a reader whose first sight of the page comes before the fetch has
 * finished. So the site is never empty and never waits, and an edit made in
 * Airtable replaces what the code says as soon as it arrives.
 */

const COLLECTIONS = [
  'publications', 'events', 'teaching', 'films',
  'editorial', 'twentyEightDaysStills', 'gallery', 'navigation',
];

const orBundled = (rows, bundled) => (rows?.length ? rows : bundled);

/*
 * The floor. content.json is a snapshot of the base that can optionally be
 * taken before a deploy (npm run content); where it holds nothing, which is the
 * ordinary case, the copy written into the app stands instead.
 */
const BASE = {
  publications: orBundled(snapshot.publications, fallbackPublications),
  events: orBundled(snapshot.events, fallbackEvents),
  teaching: orBundled(snapshot.teaching, fallbackTeaching),
  films: orBundled(snapshot.films, fallbackFilms),
  editorial: orBundled(snapshot.editorial, fallbackEditorial),
  twentyEightDaysStills: orBundled(snapshot.twentyEightDaysStills, fallbackTwentyEightDaysStills),
  gallery: orBundled(snapshot.gallery, fallbackGallery),
  navigation: orBundled(snapshot.navigation, fallbackNavigation),
  /* Key by key rather than wholesale, so a line added to the app but not yet
     written in Airtable still has words to show. */
  siteContent: { ...fallbackSiteContent, ...snapshot.siteContent },
  siteImages: snapshot.siteImages || {},
};

const overlay = (airtable) => {
  const next = { ...BASE };

  COLLECTIONS.forEach((key) => {
    if (airtable[key]?.length) {
      next[key] = airtable[key];
    }
  });

  if (airtable.siteContent) {
    next.siteContent = { ...BASE.siteContent, ...airtable.siteContent };
  }
  if (airtable.siteImages) {
    next.siteImages = { ...BASE.siteImages, ...airtable.siteImages };
  }

  return next;
};

/*
 * What the browser already knows before anything is asked of the network. A
 * copy taken within the last couple of hours is used as it stands; an older one
 * is still shown, with a fetch going on behind it.
 */
const initialState = () => {
  if (!isAirtableConfigured()) {
    return { airtable: null, fetchedAt: null, fetching: false };
  }

  const cached = refreshRequested() ? null : readCache();
  if (!cached) {
    return { airtable: null, fetchedAt: null, fetching: true };
  }

  return {
    airtable: cached.content,
    fetchedAt: cached.fetchedAt,
    fetching: !cached.usable,
  };
};

export const ContentProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const [error, setError] = useState(null);
  /* Read inside the fetch without making the fetch depend on it. */
  const airtableRef = useRef(state.airtable);
  const { fetching } = state;

  useEffect(() => {
    if (!fetching) return undefined;

    let cancelled = false;

    const load = async () => {
      const result = await fetchContent();
      if (cancelled) return;

      if (result.rateLimited) {
        pauseFetching(result);
      }

      /*
       * Tables that answered are kept over whatever was already held, so a load
       * that only half succeeded still moves the site forward, and the tables
       * that failed are asked for again on the next visit rather than being
       * recorded as empty.
       */
      if (Object.keys(result.content).length > 0) {
        const merged = { ...(airtableRef.current || {}), ...result.content };
        airtableRef.current = merged;
        saveCache(merged);
        setState({ airtable: merged, fetchedAt: Date.now(), fetching: false });
      } else {
        setState((previous) => ({ ...previous, fetching: false }));
      }

      setError(result.error);
    };

    load();

    return () => { cancelled = true; };
  }, [fetching]);

  const content = useMemo(
    () => (state.airtable ? overlay(state.airtable) : BASE),
    [state.airtable],
  );

  const value = useMemo(() => ({
    ...content,
    fetchedAt: state.fetchedAt,
    loading: state.fetching,
    usingAirtable: Boolean(state.airtable),
    error,
  }), [content, state.fetchedAt, state.fetching, state.airtable, error]);

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};
